import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Bug, Lightbulb, HelpCircle, MessageSquare, Paperclip, Loader2, X } from 'lucide-react';
import { supportService } from '../../services/supportService';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useCopy } from '../../i18n';
import { showToast } from '../common/Toast';
import type { SupportTicketCategory } from '../../types/database';

interface TicketFormProps {
  onCreated: () => void;
  onCancel: () => void;
}

/**
 * O `id` é o valor gravado na coluna `category` e o que o ícone e a cor
 * seguem. O rótulo mora no dicionário, indexado por esse mesmo id.
 */
const CATEGORIES: { id: SupportTicketCategory; icon: typeof Bug; color: string; bgColor: string }[] = [
  { id: 'bug', icon: Bug, color: 'var(--color-danger)', bgColor: 'rgba(239,68,68,0.1)' },
  { id: 'feature_request', icon: Lightbulb, color: 'var(--color-warning)', bgColor: 'rgba(245,158,11,0.1)' },
  { id: 'question', icon: HelpCircle, color: 'var(--color-info)', bgColor: 'rgba(59,130,246,0.1)' },
  { id: 'feedback', icon: MessageSquare, color: 'var(--color-primary)', bgColor: 'rgba(126,62,238,0.1)' },
];

const TicketForm = ({ onCreated, onCancel }: TicketFormProps) => {
  const { profile } = useAuth();
  const { planName } = useSubscription();
  const copy = useCopy().account.support;
  const t = copy.form;
  const [category, setCategory] = useState<SupportTicketCategory | null>(null);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSubmit = async () => {
    if (!category || !subject.trim() || !message.trim()) return;

    setSubmitting(true);
    try {
      const ticket = await supportService.createTicket({
        subject: subject.trim(),
        category,
        message: message.trim(),
        user_plan: planName || 'free',
        user_email: profile?.email || undefined,
        user_name: profile?.full_name || undefined,
        priority: planName === 'professional' || planName === 'business' ? 'high' : 'medium',
      });

      // Upload attachments to first message if any
      if (files.length > 0) {
        const { messages } = await supportService.getTicketWithMessages(ticket.id);
        if (messages[0]) {
          await Promise.all(files.map(f => supportService.uploadAndLinkAttachment(messages[0].id, f)));
        }
      }

      showToast(t.created, 'success');
      onCreated();
    } catch (err) {
      console.error('Error creating ticket:', err);
      showToast(t.createError, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newFiles = Array.from(e.target.files || []);
    const allowedTypes = ['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'application/pdf'];
    // Validate type and size (max 10MB)
    const validFiles = newFiles.filter(f => {
      if (!allowedTypes.includes(f.type)) return false;
      if (f.size > 10 * 1024 * 1024) return false;
      return true;
    });
    if (validFiles.length < newFiles.length) {
      showToast(t.invalidFiles, 'info');
    }
    setFiles(prev => [...prev, ...validFiles].slice(0, 3));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="p-5 space-y-4">
      {/* Step 1: Category */}
      <div>
        <label className="text-sm font-medium text-[var(--color-text)] mb-2 block">
          {t.categoryLabel}
        </label>
        <div className="support-category-grid">
          {CATEGORIES.map(cat => {
            const Icon = cat.icon;
            return (
              <button
                key={cat.id}
                className={`support-category-card ${category === cat.id ? 'selected' : ''}`}
                onClick={() => setCategory(cat.id)}
              >
                <div className="category-icon" style={{ background: cat.bgColor, color: cat.color }}>
                  <Icon size={18} />
                </div>
                <span className="text-xs font-medium text-[var(--color-text)]">{copy.categories[cat.id]}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Step 2: Subject */}
      <div>
        <label className="text-sm font-medium text-[var(--color-text)] mb-1.5 block">
          {t.subjectLabel}
        </label>
        <Input
          value={subject}
          onChange={e => setSubject(e.target.value)}
          placeholder={t.subjectPlaceholder}
          maxLength={200}
        />
      </div>

      {/* Step 3: Description */}
      <div>
        <label className="text-sm font-medium text-[var(--color-text)] mb-1.5 block">
          {t.messageLabel}
        </label>
        <textarea
          className="w-full min-h-[120px] p-3 border border-[var(--color-border)] rounded-[var(--radius-md,6px)] bg-[var(--color-bg-secondary)] text-[var(--color-text)] text-sm font-[inherit] leading-relaxed resize-y focus:outline-none focus:border-[var(--color-primary)]"
          value={message}
          onChange={e => setMessage(e.target.value)}
          placeholder={t.messagePlaceholder}
          maxLength={5000}
        />
        <span className="text-[11px] text-[var(--color-text-muted)] mt-1 block text-right">
          {message.length}/5000
        </span>
      </div>

      {/* Attachments */}
      <div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,.pdf"
          multiple
          className="hidden"
          onChange={handleFileChange}
        />
        {files.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-2">
            {files.map((file, i) => (
              <div key={i} className="support-attachment">
                <Paperclip size={12} />
                <span className="truncate max-w-[150px]">{file.name}</span>
                <button onClick={() => removeFile(i)} className="ml-1 hover:text-[var(--color-danger)]">
                  <X size={12} />
                </button>
              </div>
            ))}
          </div>
        )}
        {files.length < 3 && (
          <Button
            variant="ghost"
            size="sm"
            className="text-xs text-[var(--color-text-secondary)]"
            onClick={() => fileInputRef.current?.click()}
          >
            <Paperclip size={14} />
            {t.attach}
          </Button>
        )}
      </div>

      {/* Actions */}
      <div className="flex gap-3 pt-2">
        <Button variant="outline" onClick={onCancel} className="flex-1">
          {t.cancel}
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!category || !subject.trim() || !message.trim() || submitting}
          className="flex-1"
        >
          {submitting ? <Loader2 size={16} className="animate-spin" /> : null}
          {submitting ? t.sending : t.submit}
        </Button>
      </div>
    </div>
  );
};

export default TicketForm;
